import { HttpErrorResponse } from '@angular/common/http';
import type { ErrorEvent } from '@sentry/core';
import { sessionAuthFailureMatchesSentryMessage } from '../http/session-auth-failure';

type SentryLikeStackFrame = {
  filename?: string;
  abs_path?: string;
  module?: string;
  function?: string;
};

type SentryLikeExceptionValue = {
  type?: string;
  value?: string;
  /** Presente no `beforeSend` do SDK browser; a tag `mechanism` só chega no ingest. */
  mechanism?: { type?: string };
  stacktrace?: {
    frames?: SentryLikeStackFrame[];
  };
};

/** Subconjunto de {@link ErrorEvent} usado pelos filtros `beforeSend` (browser e Node). */
export type SentryLikeEvent = Pick<
  ErrorEvent,
  'exception' | 'tags' | 'contexts' | 'request' | 'fingerprint' | 'level'
>;

const RD_TRACKER_INDEXED_DB_REFERENCE_PATTERNS = [
  "Can't find variable: indexedDB",
  'indexedDB is not defined',
];

/** Safari/WebKit: `IDBOpenDBRequest.result` undefined no `onupgradeneeded` do RDTrackerDB. */
const RD_TRACKER_INDEXED_DB_TYPE_PATTERNS = ['objectStoreNames', 'RDTrackerDB'];

function stackFramesMentionRdTracker(frames: SentryLikeStackFrame[] | undefined): boolean {
  if (!frames?.length) {
    return false;
  }
  return frames.some((frame) => {
    const blob = [frame.filename, frame.abs_path, frame.module].filter(Boolean).join(' ');
    return /rdtracker/i.test(blob);
  });
}

const IN_APP_BROWSER_NAMES = new Set(['Instagram', 'Facebook']);

const IN_APP_BRIDGE_ERROR_PATTERNS = [
  'Java object is gone',
  'Error invoking postMessage',
  'Error invoking enableDidUserTypeOnKeyboardLogging',
  // Meta iOS in-app browser: LCP nativo via WKWebView (`sendDataToNative`) sem bridge.
  // Issue de referência: NX-UI-WEBAPP-ANGULAR-1HK.
  'window.webkit.messageHandlers',
  // WebKit iOS quando `window.webkit` inteiro é undefined (hxp-chat-suppression / IAB bridge).
  // Issue de referência: NX-UI-WEBAPP-ANGULAR-1HQ.
  "evaluating 'window.webkit",
];

const IN_APP_BRIDGE_STACK_FUNCTION_PATTERNS = [
  'sendDataToNative',
  'sendJsBlockingTimeMessage',
  'processLargestContentfulPaintEvent',
  // Script injetado pelo Instagram IAB (`hxp-chat-suppression`). NX-UI-WEBAPP-ANGULAR-1HQ.
  'setupIosCallbackHandler',
  // Logger de performance do IAB Android (`iabjs://`). NX-UI-WEBAPP-ANGULAR-1HN.
  'navigation_performance_logger_android',
  'iabjs://',
];

function stackFramesMentionInAppBridgeInternals(
  frames: SentryLikeStackFrame[] | undefined,
): boolean {
  if (!frames?.length) {
    return false;
  }
  return frames.some((frame) => {
    const blob = [frame.filename, frame.abs_path, frame.module, frame.function]
      .filter(Boolean)
      .join(' ');
    return IN_APP_BRIDGE_STACK_FUNCTION_PATTERNS.some((name) => blob.includes(name));
  });
}

function resolveInAppBrowserName(event: SentryLikeEvent): string | null {
  const fromTag = event.tags?.['browser.name'];
  if (typeof fromTag === 'string' && IN_APP_BROWSER_NAMES.has(fromTag)) {
    return fromTag;
  }

  const browserContext = event.contexts?.['browser'];
  const fromContext =
    typeof browserContext === 'object' && browserContext !== null
      ? (browserContext as { name?: string })['name']
      : undefined;
  if (typeof fromContext === 'string' && IN_APP_BROWSER_NAMES.has(fromContext)) {
    return fromContext;
  }

  const userAgent =
    event.request?.headers?.['User-Agent'] ??
    (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (/\bInstagram\b/i.test(userAgent)) {
    return 'Instagram';
  }
  if (/\bFBAN\b|\bFBAV\b|\bFacebook\b/i.test(userAgent)) {
    return 'Facebook';
  }

  return null;
}

function errorValueMatchesInAppBridgeMessage(errorValue: SentryLikeExceptionValue): boolean {
  return IN_APP_BRIDGE_ERROR_PATTERNS.some((pattern) => errorValue.value?.includes(pattern));
}

/**
 * Erros de bridge nativo em WebViews do Instagram/Facebook — scripts injetados pelo
 * app tentam falar com Java/Android ou WKWebView sem o handler disponível.
 * Referências: NX-UI-WEBAPP-ANGULAR-1HK, 1HQ, 1HN.
 */
export function shouldIgnoreInAppBrowserBridgeError(event: SentryLikeEvent): boolean {
  const errorValues = event.exception?.values ?? [];
  if (errorValues.length === 0) {
    return false;
  }

  const inAppBrowser = resolveInAppBrowserName(event) != null;

  return errorValues.some((errorValue) => {
    const matchesBridgeMessage = errorValueMatchesInAppBridgeMessage(errorValue);
    const matchesBridgeStack = stackFramesMentionInAppBridgeInternals(errorValue.stacktrace?.frames);

    // Stack com scripts `iabjs://` + mensagem de bridge = ruído Meta, mesmo sem tag de browser
    // no `beforeSend` (tag `browser.name` costuma ser enriquecida só no servidor).
    if (matchesBridgeMessage && matchesBridgeStack) {
      return true;
    }

    if (!inAppBrowser) {
      return false;
    }

    return matchesBridgeMessage || matchesBridgeStack;
  });
}

function resolveApiHostname(apiBaseUrl: string): string | null {
  try {
    return new URL(apiBaseUrl).hostname;
  } catch {
    return null;
  }
}

function clientNetworkTypeErrorMessage(message: string, apiHostname: string): boolean {
  if (!message.includes(apiHostname)) {
    return false;
  }
  return isGenericClientFetchNetworkMessage(message);
}

/** Chromium / WebKit: falha de rede no fetch nativo (sem HttpClient). */
function isGenericClientFetchNetworkMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower === 'failed to fetch' ||
    lower.includes('failed to fetch') ||
    lower.includes('load failed') ||
    lower.includes('networkerror when attempting to fetch resource')
  );
}

function stackFramesMentionSwUpdateService(frames: SentryLikeStackFrame[] | undefined): boolean {
  if (!frames?.length) {
    return false;
  }
  return frames.some((frame) => {
    const blob = [frame.filename, frame.abs_path, frame.module].filter(Boolean).join(' ');
    return /sw-update\.service/i.test(blob);
  });
}

function errorStackMentionsSwUpdateService(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return /sw-update\.service/i.test(error.stack ?? '');
}

/** Status de infraestrutura/rede na API própria — não são regressões de frontend. */
const IGNORABLE_OWN_API_HTTP_STATUSES = new Set([0, 502, 503, 504]);

function isIgnorableOwnApiHttpStatus(status: number): boolean {
  return IGNORABLE_OWN_API_HTTP_STATUSES.has(status);
}

function ownApiHttpFailureFromMessage(message: string, apiHostname: string): boolean {
  if (!message.includes(apiHostname) || !message.includes('Http failure response for')) {
    return false;
  }
  return /:\s*(0|502|503|504)(\s|$|,|;)/.test(message);
}

function clientNetworkHttpFailureMessage(message: string, apiHostname: string): boolean {
  return ownApiHttpFailureFromMessage(message, apiHostname);
}

function isHttpErrorResponseLike(error: unknown): error is {
  status: number;
  url?: string | null;
  name?: string;
  ok?: boolean;
} {
  if (error instanceof HttpErrorResponse) {
    return true;
  }
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const candidate = error as Record<string, unknown>;
  return (
    typeof candidate['status'] === 'number' &&
    (typeof candidate['url'] === 'string' ||
      candidate['url'] === null ||
      typeof candidate['ok'] === 'boolean') &&
    (candidate['name'] === 'HttpErrorResponse' || 'headers' in candidate)
  );
}

function isIgnorableOwnApiHttpError(error: unknown, apiHostname: string): boolean {
  if (!isHttpErrorResponseLike(error)) {
    return false;
  }
  if (!isIgnorableOwnApiHttpStatus(error.status)) {
    return false;
  }
  const url = error.url;
  return typeof url === 'string' && url.includes(apiHostname);
}

/**
 * Falha de rede/gateway do HttpClient (status 0, 502, 503, 504) contra a API própria —
 * não enviar ao Sentry no ErrorHandler (inclui objetos plain no formato HttpErrorResponse).
 */
export function isIgnorableClientApiNetworkFailure(error: unknown, apiBaseUrl: string): boolean {
  const apiHostname = resolveApiHostname(apiBaseUrl);
  if (!apiHostname) {
    return false;
  }

  let current: unknown = error;
  for (let depth = 0; depth < 4 && current != null; depth++) {
    if (isIgnorableOwnApiHttpError(current, apiHostname)) {
      return true;
    }

    if (
      current instanceof Error &&
      (clientNetworkTypeErrorMessage(current.message, apiHostname) ||
        clientNetworkHttpFailureMessage(current.message, apiHostname))
    ) {
      return true;
    }

    if (typeof current === 'object' && current !== null && 'originalError' in current) {
      current = (current as { originalError?: unknown }).originalError;
      continue;
    }

    break;
  }

  return false;
}

/**
 * Ignora falhas de rede no cliente contra a API própria (fetch status 0).
 * Mensagens comuns: Chromium "Failed to fetch", WebKit "Load failed (host)".
 * Não são regressões de código: offline, extensões, DNS, firewall, etc.
 */
/**
 * 401/403 de sessão que o interceptor já tratou (redirect + toast).
 * 403 de permissão não entra no registro e continua no Sentry.
 */
export function shouldIgnoreHandledSessionAuthFailure(event: SentryLikeEvent): boolean {
  const errorValues = event.exception?.values ?? [];
  return errorValues.some((errorValue) => {
    const type = errorValue.type ?? '';
    const value = errorValue.value ?? '';
    const looksLikeHttp =
      type === 'HttpErrorResponse' || value.includes('Http failure response for');
    return looksLikeHttp && sessionAuthFailureMatchesSentryMessage(value);
  });
}

export function shouldIgnoreClientNetworkFailureToOwnApi(
  event: SentryLikeEvent,
  apiHostname: string,
): boolean {
  if (!apiHostname) {
    return false;
  }

  const errorValues = event.exception?.values ?? [];
  return errorValues.some((errorValue) => {
    const type = errorValue.type ?? '';
    const value = errorValue.value ?? '';
    if (!value.includes(apiHostname)) {
      return false;
    }
    if (ownApiHttpFailureFromMessage(value, apiHostname)) {
      return true;
    }
    if (type !== 'TypeError') {
      return false;
    }
    return clientNetworkTypeErrorMessage(value, apiHostname);
  });
}

/**
 * Chrome/Android (e alguns WebViews) podem negar acesso a `window.localStorage`
 * e `window.sessionStorage` (SecurityError DOMException 18 — "Access is denied
 * for this document"). Não indica bug de negócio; costuma ser política de
 * privacidade, contexto particionado ou navegação vinda de apps externos.
 * Defesa em profundidade no beforeSend — o helper de storage ainda precisa
 * envolver o getter em try (typeof sessionStorage dispara o getter).
 */
export function shouldIgnoreBlockedWebStorageAccess(event: SentryLikeEvent): boolean {
  const errorValues = event.exception?.values ?? [];
  return errorValues.some((errorValue) => {
    const type = errorValue.type ?? '';
    const value = errorValue.value ?? '';
    if (type !== 'SecurityError' && type !== 'DOMException') {
      return false;
    }
    const lower = value.toLowerCase();
    const isWebStorage =
      lower.includes('localstorage') || lower.includes('sessionstorage');
    return (
      isWebStorage &&
      (lower.includes('access is denied') || lower.includes('denied for this document'))
    );
  });
}

/**
 * O snippet oficial da RD Station (loader em cloudfront) registra `onload` e chama
 * globais (`RdstationFormsIntegration`, `LeadTracking`, `TrafficSourceCookie`).
 * Com bloqueador de anúncios/privacy ou resposta vazia, o `onload` dispara sem o
 * script ter definido o global — ReferenceError em script de terceiros, não bug
 * da aplicação Angular. Ver `AppComponent.loadRdStationScriptSafely`.
 * O tracker RD (`rdtracker.min.js`) também pode falhar em webviews que não expõem
 * `indexedDB` (ex.: Instagram iOS), gerando ReferenceError fora do nosso código.
 * No Safari iOS o IndexedDB existe, mas `onupgradeneeded` chega com `result`
 * undefined — TypeError em `objectStoreNames` (NX-UI-WEBAPP-ANGULAR-1N7).
 */
export function shouldIgnoreRdStationLoaderReferenceError(event: SentryLikeEvent): boolean {
  const errorValues = event.exception?.values ?? [];
  return errorValues.some((errorValue) => {
    const type = errorValue.type ?? '';
    const value = errorValue.value ?? '';
    const rdStack = stackFramesMentionRdTracker(errorValue.stacktrace?.frames);

    if (type === 'ReferenceError') {
      if (
        value.includes('RdstationFormsIntegration is not defined') ||
        value.includes('LeadTracking is not defined') ||
        value.includes('TrafficSourceCookie is not defined')
      ) {
        return true;
      }
      if (RD_TRACKER_INDEXED_DB_REFERENCE_PATTERNS.some((pattern) => value.includes(pattern))) {
        return true;
      }
      return rdStack;
    }

    if (type === 'TypeError') {
      const isIdbUpgradeNoise = RD_TRACKER_INDEXED_DB_TYPE_PATTERNS.some((pattern) =>
        value.includes(pattern),
      );
      return isIdbUpgradeNoise && rdStack;
    }

    return false;
  });
}

/**
 * Router do Angular (NG04002): segmento da URL sem rota filha — comum com bots,
 * links quebrados ou módulos lazy sem `**`. O SSR já devolve 404 em server.ts;
 * não é bug acionável no Sentry. Ver NX-UI-WEBAPP-ANGULAR-1G1.
 */
export function shouldIgnoreAngularRouterNoMatchError(event: SentryLikeEvent): boolean {
  return (event.exception?.values ?? []).some((errorValue) => {
    const blob = [errorValue.type, errorValue.value].filter(Boolean).join(' ');
    return blob.includes('NG04002');
  });
}

export function isAngularRouterNoMatchError(error: unknown): boolean {
  if (error == null) {
    return false;
  }
  if (typeof error === 'string') {
    return error.includes('NG04002');
  }
  if (error instanceof Error) {
    if (error.message.includes('NG04002')) {
      return true;
    }
    return isAngularRouterNoMatchError(error.cause);
  }
  if (typeof error === 'object') {
    const wrapped = error as { message?: unknown; originalError?: unknown };
    if (typeof wrapped.message === 'string' && wrapped.message.includes('NG04002')) {
      return true;
    }
    if (wrapped.originalError !== undefined) {
      return isAngularRouterNoMatchError(wrapped.originalError);
    }
  }
  return false;
}

function isMalformedPercentEncodingMessage(message: string): boolean {
  if (!message) {
    return false;
  }
  if (message.includes('Failed to decode param')) {
    return true;
  }
  if (/URI malformed/i.test(message)) {
    return true;
  }
  return /%c[01]/i.test(message);
}

function isExpressInvalidUrlParamStatus(error: object): boolean {
  const candidate = error as { status?: unknown; statusCode?: unknown };
  return candidate.status === 400 || candidate.statusCode === 400;
}

/**
 * Express `decode_param` / `decodeURIComponent`: percent-encoding inválido
 * (`%c0`/`%c1` overlong, `%ZZ`). Scanner no SSR — não é regressão de produto.
 * Ref.: NX-UI-WEBAPP-ANGULAR-1MQ.
 */
export function shouldIgnoreSsrMalformedUrlError(event: SentryLikeEvent): boolean {
  return (event.exception?.values ?? []).some((errorValue) => {
    if ((errorValue.type ?? '') !== 'URIError') {
      return false;
    }
    return isMalformedPercentEncodingMessage(errorValue.value ?? '');
  });
}

/** Mesmo contrato de `shouldIgnoreSsrMalformedUrlError` para o Error vivo no Express. */
export function isSsrMalformedUrlError(error: unknown): boolean {
  if (error == null) {
    return false;
  }

  if (error instanceof URIError) {
    if (
      isMalformedPercentEncodingMessage(error.message) ||
      isExpressInvalidUrlParamStatus(error)
    ) {
      return true;
    }
    return isSsrMalformedUrlError(error.cause);
  }

  if (error instanceof Error) {
    if (error.name === 'URIError' && isMalformedPercentEncodingMessage(error.message)) {
      return true;
    }
    if (error.cause !== undefined) {
      return isSsrMalformedUrlError(error.cause);
    }
  }

  if (typeof error === 'object') {
    const wrapped = error as {
      name?: unknown;
      message?: unknown;
      originalError?: unknown;
    };
    if (
      wrapped.name === 'URIError' &&
      typeof wrapped.message === 'string' &&
      isMalformedPercentEncodingMessage(wrapped.message)
    ) {
      return true;
    }
    if (wrapped.originalError !== undefined) {
      return isSsrMalformedUrlError(wrapped.originalError);
    }
  }

  return false;
}

/**
 * Chrome Mobile em Android pode emitir `Error: illegal access` sem stack útil —
 * bug/limitação do motor ou extensão de plataforma, não fluxo de negócio da app.
 * Issue de referência: NX-UI-WEBAPP-ANGULAR-1G5 (multimarcas, 1 ocorrência, sem frames).
 */
export function shouldIgnoreChromeAndroidIllegalAccessNoise(event: SentryLikeEvent): boolean {
  if (event.tags?.['os.name'] !== 'Android' || event.tags?.['browser.name'] !== 'Chrome Mobile') {
    return false;
  }

  return (event.exception?.values ?? []).some((errorValue) => {
    const type = errorValue.type ?? 'Error';
    if (type !== 'Error') {
      return false;
    }
    const raw = (errorValue.value ?? '').trim().toLowerCase();
    const normalized = raw.replace(/^error:\s*/i, '').trim();
    return normalized === 'illegal access';
  });
}

function sentryEventHasAnyStackFrames(event: SentryLikeEvent): boolean {
  for (const errorValue of event.exception?.values ?? []) {
    const frames = errorValue.stacktrace?.frames;
    if (frames && frames.length > 0) {
      return true;
    }
  }
  return false;
}

function sentryStackFramesReferenceAppBundles(event: SentryLikeEvent): boolean {
  for (const errorValue of event.exception?.values ?? []) {
    for (const frame of errorValue.stacktrace?.frames ?? []) {
      const blob = [frame.filename, frame.abs_path, frame.module].filter(Boolean).join(' ');
      if (/\.js($|\?)/i.test(blob) || /chunk-|main-|polyfills-|scripts-/.test(blob)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * WebViews do Facebook/Instagram podem falhar ao parsear scripts inline ou bundles
 * truncados (cache/rede) — SyntaxError sem frames dos chunks da aplicação.
 * Issue de referência: NX-UI-WEBAPP-ANGULAR-1HG (multimarcas, Facebook Android).
 */
export function shouldIgnoreInAppBrowserSyntaxErrorWithoutAppStack(
  event: SentryLikeEvent,
): boolean {
  if (event.tags?.['mechanism'] !== 'onerror') {
    return false;
  }

  const browserName = event.tags?.['browser.name'];
  if (typeof browserName !== 'string' || !IN_APP_BROWSER_NAMES.has(browserName)) {
    return false;
  }

  const hasSyntaxError = (event.exception?.values ?? []).some((errorValue) => {
    if (errorValue.type !== 'SyntaxError') {
      return false;
    }
    const value = (errorValue.value ?? '').trim();
    return value === 'Unexpected end of input';
  });

  if (!hasSyntaxError) {
    return false;
  }

  return !sentryStackFramesReferenceAppBundles(event);
}

/**
 * Checagem periódica de `/ngsw.json` em `SwUpdateService` (fallback de versão PWA).
 * Falhas de rede, extensões do Chrome ou offline não são regressão de negócio.
 * Referência: NX-UI-WEBAPP-ANGULAR-1HT.
 */
export function shouldIgnoreNgswVersionCheckNetworkFailure(event: SentryLikeEvent): boolean {
  return (event.exception?.values ?? []).some((errorValue) => {
    if (errorValue.type !== 'TypeError') {
      return false;
    }
    const value = errorValue.value ?? '';
    if (!isGenericClientFetchNetworkMessage(value)) {
      return false;
    }
    return stackFramesMentionSwUpdateService(errorValue.stacktrace?.frames);
  });
}

/**
 * Mesmo critério de `shouldIgnoreNgswVersionCheckNetworkFailure` para o ErrorHandler.
 */
export function isIgnorableNgswVersionCheckNetworkFailure(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 4 && current != null; depth++) {
    if (
      current instanceof Error &&
      isGenericClientFetchNetworkMessage(current.message) &&
      errorStackMentionsSwUpdateService(current)
    ) {
      return true;
    }

    if (typeof current === 'object' && current !== null && 'originalError' in current) {
      current = (current as { originalError?: unknown }).originalError;
      continue;
    }

    break;
  }

  return false;
}

/**
 * Após deploy, o browser pode manter `index.html`/ngsw cache antigo e falhar ao
 * buscar chunks com hash novo (ou abortar import durante reload precoce do SW).
 * Chromium: "Failed to fetch dynamically imported module";
 * Firefox: "error loading dynamically imported module";
 * Safari iOS: "Importing a module script failed" (sem stack).
 * Ref.: NX-UI-WEBAPP-ANGULAR-1HZ / 1JV / 1JQ / 1JS / 1JY.
 */
export const WOSI_LAZY_CHUNK_RELOAD_SESSION_KEY = 'wosi_lazy_chunk_reload';

/** Flag pós-1ª falha (reload agendado / página recém-recarregada). */
export const WOSI_LAZY_CHUNK_RELOAD_FLAG = '1';

/** Flag após 2ª falha na mesma sessão — recovery esgotado (não loopa). */
export const WOSI_LAZY_CHUNK_RECOVERY_EXHAUSTED = 'exhausted';

/** Query do ngsw Angular para o documento ignorar o SW. Chunks: unregister. Ref.: 1JY. */
export const WOSI_NGSW_BYPASS_QUERY = 'ngsw-bypass';

/**
 * Guarda same-tick: `unhandledrejection` + `ErrorHandler` chamam o recovery juntos.
 * Sem isso a 2ª chamada limpa a flag e reporta falso "exhausted" no 1º reload.
 */
let lazyChunkRecoveryInFlight = false;

/** Fallback quando `sessionStorage` está bloqueado (1 tentativa por page load). */
let lazyChunkRecoveryMemoryAttempted = false;

/**
 * Getter de `window.sessionStorage` pode lançar SecurityError (DOMException 18)
 * no Chromium/WebView — `typeof sessionStorage` já dispara o getter.
 * SSR: `window` ausente → null, sem tocar no getter.
 */
function getSessionStorageOrNull(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Navegação dura com `ngsw-bypass` — `location.reload()` no Chrome Mobile WebView
 * frequentemente não troca o documento controlado pelo SW (1JY).
 */
export const lazyChunkRecoveryNav = {
  reload(): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(WOSI_NGSW_BYPASS_QUERY, '1');
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  },
};

function unregisterServiceWorkerRegistration(registration: {
  unregister: () => Promise<boolean>;
}): Promise<boolean> {
  return registration.unregister().catch(() => false);
}

/**
 * Antes do reload: `registration.update()` (best-effort) e **unregister** para o
 * próximo load não ser interceptado por SW stale. Não postar `SKIP_WAITING`
 * (no-op no ngsw). Não chamar `ACTIVATE_UPDATE` (race Safari 1JS).
 */
export const lazyChunkRecoverySw = {
  prepareForReload(): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return Promise.resolve();
    }
    return navigator.serviceWorker
      .getRegistration()
      .then(async (registration) => {
        if (!registration) {
          return;
        }
        try {
          await registration.update();
        } catch {
          /* ngsw.json/rede — 1JY WebView */
        }
        await unregisterServiceWorkerRegistration(registration);
      })
      .catch(() => undefined);
  },

  unregister(): Promise<void> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return Promise.resolve();
    }
    return navigator.serviceWorker
      .getRegistration()
      .then(async (registration) => {
        if (!registration) {
          return;
        }
        await unregisterServiceWorkerRegistration(registration);
      })
      .catch(() => undefined);
  },
};

/** Reset de estado in-memory (apenas specs). */
export function resetLazyChunkRecoveryStateForTests(): void {
  lazyChunkRecoveryInFlight = false;
  lazyChunkRecoveryMemoryAttempted = false;
}

function scheduleLazyChunkRecoveryReload(): void {
  lazyChunkRecoveryInFlight = true;
  const timeoutMs = 1500;
  let timedOut = false;
  void Promise.race([
    lazyChunkRecoverySw.prepareForReload(),
    new Promise<void>((resolve) => {
      setTimeout(() => {
        timedOut = true;
        resolve();
      }, timeoutMs);
    }),
  ]).finally(() => {
    const navigate = () => lazyChunkRecoveryNav.reload();
    if (!timedOut) {
      navigate();
      return;
    }
    // update() pendurado (ngsw.json) — desregistra o SW antes da nav (1JY).
    void lazyChunkRecoverySw.unregister().finally(navigate);
  });
}

function collectErrorChain(error: unknown, maxDepth: number): unknown[] {
  const out: unknown[] = [];
  let current: unknown = error;
  for (let depth = 0; depth < maxDepth && current != null; depth++) {
    out.push(current);

    if (typeof current === 'object' && current !== null && 'originalError' in current) {
      const next = (current as { originalError?: unknown }).originalError;
      if (next !== undefined) {
        current = next;
        continue;
      }
    }

    if (current instanceof Error && current.cause !== undefined) {
      current = current.cause;
      continue;
    }

    break;
  }
  return out;
}

function errorMessageFromUnknown(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const m = (error as { message?: unknown }).message;
    return typeof m === 'string' ? m : '';
  }
  return '';
}

function isLazyChunkLoadFailureMessage(message: string): boolean {
  if (!message) {
    return false;
  }
  // Chromium: "Failed to fetch…"; Firefox: "error loading…" (1JV).
  if (/(?:Failed to fetch|error loading) dynamically imported module/i.test(message)) {
    return true;
  }
  // Safari / Mobile Safari (1JS) — TypeError sem stacktrace.
  if (/Importing a module script failed/i.test(message)) {
    return true;
  }
  if (/Loading chunk [\w-]+ failed/i.test(message)) {
    return true;
  }
  return false;
}

/**
 * Falha típica de lazy `import()` quando o bundle referenciado não existe mais (deploy).
 */
export function isLazyChunkLoadFailure(error: unknown): boolean {
  return collectErrorChain(error, 8).some((node) => {
    if (node instanceof Error && node.name === 'ChunkLoadError') {
      return true;
    }
    return isLazyChunkLoadFailureMessage(errorMessageFromUnknown(node));
  });
}

/** Detecta lazy chunk failure já serializado no payload do `beforeSend`. */
export function isLazyChunkLoadFailureEvent(event: SentryLikeEvent): boolean {
  return (event.exception?.values ?? []).some((errorValue) => {
    if (errorValue.type === 'ChunkLoadError') {
      return true;
    }
    return isLazyChunkLoadFailureMessage(errorValue.value ?? '');
  });
}

/**
 * A 1ª falha é recuperada com reload (ErrorHandler / unhandledrejection) e não chega ao Sentry.
 * A 2ª (recovery esgotado) chega ao `beforeSend` — não filtrar: fingerprint estável (sem hash do
 * chunk), tag `lazy_chunk_recovery=exhausted` e level `warning` para triagem sem alerta de bug
 * de aplicação. Ref.: NX-UI-WEBAPP-ANGULAR-1JQ / 1HZ / 1JS / 1JY.
 */
export function enrichLazyChunkLoadFailureEvent<T extends SentryLikeEvent>(event: T): T {
  if (!isLazyChunkLoadFailureEvent(event)) {
    return event;
  }

  event.fingerprint = ['wosi-lazy-chunk-load-failure'];
  event.level = 'warning';
  event.tags = {
    ...(event.tags ?? {}),
    lazy_chunk_recovery: 'exhausted',
  };

  return event;
}

/** Mensagem Node ESM: Cannot find module .../server/chunk-XXXX.mjs (SSR stale pós-deploy). */
export function isSsrStaleChunkErrorMessage(message: string): boolean {
  if (!message) {
    return false;
  }
  const isModuleNotFound = /Cannot find module/i.test(message) || /ERR_MODULE_NOT_FOUND/i.test(message);
  if (!isModuleNotFound) {
    return false;
  }
  return /[/\\]server[/\\]chunk-[A-Z0-9]+\.mjs/i.test(message) || /chunk-[A-Z0-9]+\.mjs/i.test(message);
}

function ssrStaticAssetNotFoundEventBlob(event: SentryLikeEvent): string {
  const exceptionBlob = (event.exception?.values ?? [])
    .map((errorValue) => [errorValue.type, errorValue.value].filter(Boolean).join(' '))
    .join(' ');
  const requestUrl = typeof event.request?.url === 'string' ? event.request.url : '';
  return `${exceptionBlob} ${requestUrl}`.trim();
}

function isSsrStaticAssetEnoentBlob(blob: string): boolean {
  return blob.includes('ENOENT') && blob.includes('no such file or directory');
}

/** Path de asset no ENOENT (`stat '.../browser/assets/...'`) ou na tag/request. */
function blobMentionsBrowserAssetsPath(blob: string): boolean {
  return /[/\\]browser[/\\]assets[/\\]/.test(blob) || /(?:^|[/?])assets\//.test(blob);
}

function ssrRequestPathFromEvent(event: SentryLikeEvent): string {
  const tagged = event.tags?.['ssr.path'];
  if (typeof tagged === 'string' && tagged) {
    return tagged;
  }
  return typeof event.request?.url === 'string' ? event.request.url : '';
}

/**
 * Payloads típicos de scanner (não são assets do build Angular).
 * Ref.: NX-UI-WEBAPP-ANGULAR-1JW / 1JZ / 1K0–1K2 / 1K1 / 1M6.
 */
const SSR_SCANNER_PROBE_EXTENSION =
  /\.(php|phtml|phar|asp|aspx|jsp|cgi|exe|sh|bash|env|git)(?:\/|$|\?|#)/i;
const SSR_SCANNER_PROBE_SEGMENT =
  /(?:^|[/?])(?:wp-admin|wp-login|xmlrpc|phpinfo|file-uploader\/server\/php)(?:[/?]|$)/i;

/**
 * Segmento hidden do `send` (`dotfiles: 'ignore'`): `part.length > 1 && part[0] === '.'`.
 * Não casa `app.min.css` nem `/manifest.webmanifest`. Ref.: NX-UI-WEBAPP-ANGULAR-1M6.
 */
export function pathHasHiddenDotfileSegment(path: string): boolean {
  if (!path) {
    return false;
  }
  const pathname = (path.split('?')[0]?.split('#')[0] ?? path).replace(/['"]/g, '');
  return pathname.split(/[/\\]/).some((seg) => seg.length > 1 && seg.startsWith('.'));
}

/** `http-errors` `createError(404)` do `send` — sem ENOENT / sem stat. Ref.: 1M6. */
function isSsrSendNotFoundBlob(blob: string): boolean {
  return /NotFoundError/i.test(blob) && /\bNot Found\b/i.test(blob);
}

export function isSsrScannerProbePath(path: string): boolean {
  if (!path) {
    return false;
  }
  // Mensagem Node: stat '.../assets/index.html' — aspas quebram âncora `$`.
  const pathname = (path.split('?')[0]?.split('#')[0] ?? path).replace(/['"]/g, '');
  if (SSR_SCANNER_PROBE_EXTENSION.test(pathname) || SSR_SCANNER_PROBE_SEGMENT.test(pathname)) {
    return true;
  }
  // Listing GET /assets ou /assets/ — dist não serve index. Ref.: 1K1 / WOSI-138.
  if (/\/assets\/?$/i.test(pathname)) {
    return true;
  }
  if (pathHasHiddenDotfileSegment(pathname)) {
    return true;
  }
  return /[/\\]assets[/\\]index\.html/i.test(pathname);
}

function eventLooksLikeSsrAssetsEnoent(event: SentryLikeEvent): boolean {
  const blob = ssrStaticAssetNotFoundEventBlob(event);
  if (!isSsrStaticAssetEnoentBlob(blob) && !isSsrSendNotFoundBlob(blob)) {
    return false;
  }

  const ssrPath = event.tags?.['ssr.path'];
  const taggedAssetsPath =
    event.tags?.['component'] === 'ssr' &&
    typeof ssrPath === 'string' &&
    (ssrPath === '/assets' || ssrPath.startsWith('/assets/'));

  return taggedAssetsPath || blobMentionsBrowserAssetsPath(blob);
}

/** ENOENT de probe em `/assets/*` — drop no beforeSend. Asset real ausente NÃO entra aqui. */
export function shouldIgnoreSsrStaticAssetNotFoundError(event: SentryLikeEvent): boolean {
  if (!eventLooksLikeSsrAssetsEnoent(event)) {
    return false;
  }

  const blob = ssrStaticAssetNotFoundEventBlob(event);
  const pathHint = ssrRequestPathFromEvent(event);
  return isSsrScannerProbePath(pathHint) || isSsrScannerProbePath(blob);
}

/**
 * ENOENT de asset real do build (css/js/img/fonte) — fingerprint estável + warning.
 * Ref.: 1JW apertado pela 1JZ (Olivia).
 */
export function enrichSsrMissingStaticAssetEvent<T extends SentryLikeEvent>(event: T): T {
  if (!eventLooksLikeSsrAssetsEnoent(event) || shouldIgnoreSsrStaticAssetNotFoundError(event)) {
    return event;
  }

  event.fingerprint = ['wosi-ssr-missing-static-asset'];
  event.level = 'warning';
  event.tags = {
    ...(event.tags ?? {}),
    component: 'ssr',
    error_type: 'ssr_missing_static_asset',
  };

  return event;
}

export function isSsrStaticAssetNotFoundError(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const nodeErr = err as {
    code?: string;
    message?: string;
    name?: string;
    status?: number;
    statusCode?: number;
  };
  const message = typeof nodeErr.message === 'string' ? nodeErr.message : '';
  const status = nodeErr.status ?? nodeErr.statusCode;
  // `send` com `dotfiles: 'ignore'` chama `createError(404)` antes do fs.stat. Ref.: 1M6.
  const isSendNotFound =
    nodeErr.name === 'NotFoundError' &&
    status === 404 &&
    (message === 'Not Found' || message === 'NotFoundError: Not Found');
  if (isSendNotFound) {
    return true;
  }
  if (!message.includes('no such file or directory')) {
    return false;
  }
  // `send`/`http-errors` pode wrappear o fs.stat e perder `.code`.
  return nodeErr.code === 'ENOENT' || message.includes('ENOENT');
}

/** Detecta ERR_MODULE_NOT_FOUND de chunk SSR hasheado (processo Node stale). Ref.: NX-UI-WEBAPP-ANGULAR-1JR. */
export function isSsrStaleChunkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const e = err as { code?: string; message?: string };
  if (e.code === 'ERR_MODULE_NOT_FOUND' && isSsrStaleChunkErrorMessage(e.message ?? '')) {
    return true;
  }
  return isSsrStaleChunkErrorMessage(e.message ?? '');
}

/** Detecta stale chunk SSR já serializado no payload do `beforeSend`. */
export function isSsrStaleChunkEvent(event: SentryLikeEvent): boolean {
  return (event.exception?.values ?? []).some((errorValue) =>
    isSsrStaleChunkErrorMessage(errorValue.value ?? ''),
  );
}

/**
 * Enriquecer eventos de chunk SSR ausente (deploy overwrite / processo stale).
 * Fingerprint estável (sem hash do chunk) + tags de ops. Ref.: NX-UI-WEBAPP-ANGULAR-1JR / WOSI-120.
 */
export function enrichSsrStaleChunkEvent<T extends SentryLikeEvent>(event: T): T {
  if (!isSsrStaleChunkEvent(event)) {
    return event;
  }

  event.fingerprint = ['wosi-ssr-stale-chunk'];
  event.tags = {
    ...(event.tags ?? {}),
    component: 'ssr',
    error_type: 'ssr_stale_chunk',
  };

  return event;
}

/**
 * Um reload força novo `index`/manifest + update do SW; evita loop com sessionStorage.
 * Retorna `true` se está recuperando (reload agendado / already in-flight) — não Sentry.
 * Retorna `false` se recovery esgotado (2ª falha pós-reload) — Sentry com tag via beforeSend.
 */
export function tryRecoverFromLazyChunkFailure(error: unknown): boolean {
  if (!isLazyChunkLoadFailure(error)) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }

  if (lazyChunkRecoveryInFlight) {
    return true;
  }

  const storage = getSessionStorageOrNull();
  if (!storage) {
    if (lazyChunkRecoveryMemoryAttempted) {
      return false;
    }
    lazyChunkRecoveryMemoryAttempted = true;
    scheduleLazyChunkRecoveryReload();
    return true;
  }

  try {
    const flag = storage.getItem(WOSI_LAZY_CHUNK_RELOAD_SESSION_KEY);
    if (
      flag === WOSI_LAZY_CHUNK_RELOAD_FLAG ||
      flag === WOSI_LAZY_CHUNK_RECOVERY_EXHAUSTED
    ) {
      storage.setItem(
        WOSI_LAZY_CHUNK_RELOAD_SESSION_KEY,
        WOSI_LAZY_CHUNK_RECOVERY_EXHAUSTED,
      );
      return false;
    }

    storage.setItem(WOSI_LAZY_CHUNK_RELOAD_SESSION_KEY, WOSI_LAZY_CHUNK_RELOAD_FLAG);
  } catch {
    if (lazyChunkRecoveryMemoryAttempted) {
      return false;
    }
    lazyChunkRecoveryMemoryAttempted = true;
  }

  scheduleLazyChunkRecoveryReload();
  return true;
}

/** True quando a sessão já esgotou o reload de lazy chunk (2ª falha). */
export function isLazyChunkRecoveryExhausted(): boolean {
  const storage = getSessionStorageOrNull();
  if (!storage) {
    return lazyChunkRecoveryMemoryAttempted && !lazyChunkRecoveryInFlight;
  }
  try {
    return (
      storage.getItem(WOSI_LAZY_CHUNK_RELOAD_SESSION_KEY) ===
      WOSI_LAZY_CHUNK_RECOVERY_EXHAUSTED
    );
  } catch {
    return lazyChunkRecoveryMemoryAttempted && !lazyChunkRecoveryInFlight;
  }
}

function stripNgswBypassQueryFromUrl(): void {
  if (typeof window === 'undefined' || typeof history === 'undefined') {
    return;
  }
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(WOSI_NGSW_BYPASS_QUERY)) {
      return;
    }
    url.searchParams.delete(WOSI_NGSW_BYPASS_QUERY);
    const search = url.searchParams.toString();
    history.replaceState(
      history.state,
      '',
      `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
    );
  } catch {
    /* URL / SecurityError */
  }
}

/**
 * Após NavigationEnd bem-sucedido: limpa só o marcador de 1ª tentativa (`1`).
 * Preserva `exhausted` na sessão — evita novo reload loop se o SW/chunk continuar stale.
 * Remove `ngsw-bypass` da URL (recovery 1JY) sem novo load.
 */
export function clearLazyChunkReloadSessionFlag(): void {
  stripNgswBypassQueryFromUrl();

  const storage = getSessionStorageOrNull();
  if (!storage) {
    return;
  }
  try {
    if (
      storage.getItem(WOSI_LAZY_CHUNK_RELOAD_SESSION_KEY) ===
      WOSI_LAZY_CHUNK_RELOAD_FLAG
    ) {
      storage.removeItem(WOSI_LAZY_CHUNK_RELOAD_SESSION_KEY);
    }
  } catch {
    /* SecurityError / quota */
  }
}

/**
 * No `beforeSend` do SDK browser, `mechanism` vive em
 * `exception.values[].mechanism.type`. A tag `mechanism` (como `browser.name`)
 * costuma ser enriquecida só no ingest — 1MG vazou porque o filtro lia só a tag.
 */
function eventMechanismIsUnhandledRejection(event: SentryLikeEvent): boolean {
  if (event.tags?.['mechanism'] === 'onunhandledrejection') {
    return true;
  }
  return (event.exception?.values ?? []).some(
    (errorValue) => errorValue.mechanism?.type === 'onunhandledrejection',
  );
}

/**
 * WebViews (Instagram, Facebook) e scripts de analytics costumam abortar `fetch`
 * (DOMException 20 / AbortError). Isso vira `unhandledrejection` sem stack da
 * aplicação — não é regressão acionável no código Angular. Referência:
 * NX-UI-WEBAPP-ANGULAR-1GK / 1MG.
 */
export function shouldIgnoreAbortUnhandledRejectionWithoutStack(
  event: SentryLikeEvent,
): boolean {
  if (!eventMechanismIsUnhandledRejection(event)) {
    return false;
  }

  const hasAbort = (event.exception?.values ?? []).some((errorValue) => {
    const type = errorValue.type ?? '';
    const value = (errorValue.value ?? '').toLowerCase();
    if (type === 'DOMException') {
      return value.includes('aborterror') || value.includes('aborted');
    }
    return value.includes('aborterror') && value.includes('aborted');
  });

  if (!hasAbort) {
    return false;
  }

  return !sentryEventHasAnyStackFrames(event);
}
